/**
 * AdminAuthors - Podstrona administracyjna do zarządzania autorami
 * wojskaspecjalne.pl
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCog } from 'lucide-react';
import { AdminOnly } from '../components/RoleGuard';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Text,
  Textarea,
  Alert,
  AlertDescription,
} from '../components/ui';
import { ConfirmDialog } from '../components/dialogs';
import { createAuthor, deleteAuthor, getAllAuthors, updateAuthor, type Author } from '../lib/libraryService';

type NewAuthorForm = {
  name: string;
  bio: string;
  website_url: string;
};

type EditAuthorForm = {
  id: string;
  name: string;
  bio: string;
  website_url: string;
};

const initialNewAuthor: NewAuthorForm = {
  name: '',
  bio: '',
  website_url: '',
};

export const AdminAuthorsTab: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAuthor, setNewAuthor] = useState<NewAuthorForm>(initialNewAuthor);

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    authorId: string;
    authorName: string;
  } | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editAuthor, setEditAuthor] = useState<EditAuthorForm | null>(null);

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 5000);
  };

  const loadAuthors = async () => {
    try {
      setLoading(true);
      const result = await getAllAuthors();
      if (!result.success) {
        showMessage('error', result.error || 'Błąd podczas ładowania autorów');
        setAuthors([]);
        return;
      }
      setAuthors(result.data || []);
    } catch (error) {
      console.error('Error loading authors:', error);
      showMessage('error', 'Błąd podczas ładowania autorów');
      setAuthors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuthors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAuthors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return authors;
    return authors.filter((author) => {
      const name = (author.name || '').toLowerCase();
      const bio = ((author as any).bio || '').toLowerCase();
      const websiteUrl = ((author as any).website_url || '').toLowerCase();
      return name.includes(q) || bio.includes(q) || websiteUrl.includes(q);
    });
  }, [authors, search]);

  const handleCreateAuthor = async () => {
    if (!newAuthor.name.trim()) {
      showMessage('error', 'Imię i nazwisko autora jest wymagane');
      return;
    }

    try {
      setCreating(true);
      const result = await createAuthor({
        name: newAuthor.name.trim(),
        bio: newAuthor.bio.trim() ? newAuthor.bio.trim() : null,
        website_url: newAuthor.website_url.trim() ? newAuthor.website_url.trim() : null,
      });

      if (!result.success || !result.data) {
        showMessage('error', result.error || 'Nie udało się dodać autora');
        return;
      }

      showMessage('success', 'Autor został dodany');
      setCreateDialogOpen(false);
      setNewAuthor(initialNewAuthor);
      await loadAuthors();
    } catch (error) {
      console.error('Error creating author:', error);
      showMessage('error', 'Błąd podczas dodawania autora');
    } finally {
      setCreating(false);
    }
  };

  const confirmDeleteAuthor = async () => {
    if (!deleteDialog) return;
    try {
      const result = await deleteAuthor(deleteDialog.authorId);
      if (!result.success) {
        showMessage('error', result.error || 'Nie udało się usunąć autora');
        return;
      }
      showMessage('success', `Autor "${deleteDialog.authorName}" został usunięty`);
      setDeleteDialog(null);
      await loadAuthors();
    } catch (error) {
      console.error('Error deleting author:', error);
      showMessage('error', 'Błąd podczas usuwania autora');
    }
  };

  const openEditDialog = (author: Author) => {
    setEditAuthor({
      id: author.id,
      name: author.name || '',
      bio: ((author as any).bio || '') as string,
      website_url: ((author as any).website_url || '') as string,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateAuthor = async () => {
    if (!editAuthor) return;
    if (!editAuthor.name.trim()) {
      showMessage('error', 'Imię i nazwisko autora jest wymagane');
      return;
    }

    try {
      setEditing(true);
      const result = await updateAuthor({
        id: editAuthor.id,
        name: editAuthor.name.trim(),
        bio: editAuthor.bio.trim() ? editAuthor.bio.trim() : null,
        website_url: editAuthor.website_url.trim() ? editAuthor.website_url.trim() : null,
      });

      if (!result.success) {
        showMessage('error', result.error || 'Nie udało się zaktualizować autora');
        return;
      }

      showMessage('success', 'Autor został zaktualizowany');
      setEditDialogOpen(false);
      setEditAuthor(null);
      await loadAuthors();
    } catch (error) {
      console.error('Error updating author:', error);
      showMessage('error', 'Błąd podczas aktualizacji autora');
    } finally {
      setEditing(false);
    }
  };

  const content = (
    <>
      <div className={embedded ? 'admin-content' : ''}>
        <div className={embedded ? 'admin-content-header' : ''}>
          {embedded ? (
            <>
              <h1>Autorzy</h1>
              <p className="content-subtitle">Zarządzaj autorami biblioteki (dodawanie, edycja, usuwanie).</p>
            </>
          ) : (
            <Box className="flex items-start justify-between gap-4 flex-col sm:flex-row">
              <Box>
                <Box className="flex items-center gap-3">
                  <UserCog size={22} aria-hidden="true" />
                  <h1>Autorzy</h1>
                </Box>
                <Text size="3" color="soft" className="mt-2">
                  Zarządzaj autorami biblioteki (dodawanie, edycja, usuwanie).
                </Text>
              </Box>
              <Box className="flex items-center gap-3">
                <Button variant="outline" onClick={() => navigate('/panel-zarzadzania')}>
                  Wróć do panelu
                </Button>
                <Button variant="solid" onClick={() => setCreateDialogOpen(true)} disabled={loading}>
                  + Dodaj autora
                </Button>
              </Box>
            </Box>
          )}
        </div>

        {embedded && (
          <div className="admin-section-header" style={{ marginTop: '1rem' }}>
            <div />
            <Button variant="solid" onClick={() => setCreateDialogOpen(true)} disabled={loading}>
              + Dodaj autora
            </Button>
          </div>
        )}

          {message && (
            <Box className={embedded ? 'admin-section' : 'mt-6'}>
              <Alert variant={message.type === 'success' ? 'success' : 'error'}>
                <AlertDescription>{message.text}</AlertDescription>
              </Alert>
            </Box>
          )}

          <Box className={embedded ? 'admin-section' : 'mt-6'}>
            <Input
              type="text"
              placeholder="Szukaj autora (imię, bio, strona)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>

          <Box className={embedded ? 'admin-section' : 'mt-6'}>
            <Card variant="soft">
              <CardContent>
                {loading ? (
                  <Text size="3" color="soft">
                    Ładowanie autorów...
                  </Text>
                ) : filteredAuthors.length === 0 ? (
                  <Text size="3" color="soft">
                    Brak autorów do wyświetlenia.
                  </Text>
                ) : (
                  <div className="admin-table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Imię i nazwisko</th>
                          <th>Strona</th>
                          <th>Akcje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAuthors.map((author) => (
                          <tr key={author.id}>
                            <td>
                              <strong>{author.name}</strong>
                              {(author as any).slug && (
                                <div className="text-sm text-[var(--text-white-70)] mt-1">
                                  slug: {(author as any).slug}
                                </div>
                              )}
                            </td>
                            <td>
                              {(author as any).website_url ? (
                                <a
                                  href={(author as any).website_url as string}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
                                >
                                  {(author as any).website_url}
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              <div className="admin-table-actions">
                                <Button
                                  variant="outline"
                                  size="2"
                                  onClick={() => openEditDialog(author)}
                                >
                                  Edytuj
                                </Button>
                                <Button
                                  variant="outline"
                                  size="2"
                                  onClick={() =>
                                    setDeleteDialog({
                                      open: true,
                                      authorId: author.id,
                                      authorName: author.name,
                                    })
                                  }
                                >
                                  Usuń
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </Box>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Dodaj autora</DialogTitle>
                <DialogDescription>Dodaj nowego autora do bazy biblioteki.</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="admin-new-author-name">Imię i nazwisko</Label>
                  <Input
                    id="admin-new-author-name"
                    type="text"
                    value={newAuthor.name}
                    onChange={(e) => setNewAuthor((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="np. Marian Mazur"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-new-author-website">Strona (opcjonalnie)</Label>
                  <Input
                    id="admin-new-author-website"
                    type="text"
                    value={newAuthor.website_url}
                    onChange={(e) =>
                      setNewAuthor((prev) => ({ ...prev, website_url: e.target.value }))
                    }
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-new-author-bio">Bio (opcjonalnie)</Label>
                  <Textarea
                    id="admin-new-author-bio"
                    value={newAuthor.bio}
                    onChange={(e) => setNewAuthor((prev) => ({ ...prev, bio: e.target.value }))}
                    placeholder="Krótka notka biograficzna..."
                    rows={5}
                  />
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" disabled={creating}>
                    Anuluj
                  </Button>
                </DialogClose>
                <Button variant="solid" onClick={handleCreateAuthor} disabled={creating}>
                  {creating ? 'Dodawanie...' : 'Dodaj autora'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <ConfirmDialog
            open={Boolean(deleteDialog?.open)}
            title="Usuń autora"
            description={
              deleteDialog
                ? `Czy na pewno chcesz usunąć autora "${deleteDialog.authorName}"?`
                : 'Czy na pewno chcesz usunąć autora?'
            }
            confirmText="Usuń"
            cancelText="Anuluj"
            onConfirm={confirmDeleteAuthor}
            onCancel={() => setDeleteDialog(null)}
            variant="destructive"
          />

          <Dialog open={editDialogOpen} onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditAuthor(null);
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edytuj autora</DialogTitle>
                <DialogDescription>Zaktualizuj dane autora w bibliotece.</DialogDescription>
              </DialogHeader>

              {editAuthor && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="admin-edit-author-name">Imię i nazwisko</Label>
                    <Input
                      id="admin-edit-author-name"
                      type="text"
                      value={editAuthor.name}
                      onChange={(e) =>
                        setEditAuthor((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                      }
                      placeholder="np. Marian Mazur"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-edit-author-website">Strona (opcjonalnie)</Label>
                    <Input
                      id="admin-edit-author-website"
                      type="text"
                      value={editAuthor.website_url}
                      onChange={(e) =>
                        setEditAuthor((prev) =>
                          prev ? { ...prev, website_url: e.target.value } : prev
                        )
                      }
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-edit-author-bio">Bio (opcjonalnie)</Label>
                    <Textarea
                      id="admin-edit-author-bio"
                      value={editAuthor.bio}
                      onChange={(e) =>
                        setEditAuthor((prev) => (prev ? { ...prev, bio: e.target.value } : prev))
                      }
                      placeholder="Krótka notka biograficzna..."
                      rows={6}
                    />
                  </div>
                </div>
              )}

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" disabled={editing}>
                    Anuluj
                  </Button>
                </DialogClose>
                <Button variant="solid" onClick={handleUpdateAuthor} disabled={editing || !editAuthor}>
                  {editing ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </div>
    </>
  );

  return (
    <AdminOnly
      fallback={
        embedded ? (
          <Alert variant="error">
            <AlertDescription>
              Zarządzanie autorami dostępne tylko dla administratorów.
            </AlertDescription>
          </Alert>
        ) : (
          <Box className="premium-page-bg">
            <Box className="premium-page-container py-10">
              <Alert variant="error">
                <AlertDescription>
                  Zarządzanie autorami dostępne tylko dla administratorów.
                </AlertDescription>
              </Alert>
            </Box>
          </Box>
        )
      }
    >
      {embedded ? content : (
        <Box className="premium-page-bg">
          <Box className="premium-page-container py-10">
            {content}
          </Box>
        </Box>
      )}
    </AdminOnly>
  );
};

export const AdminAuthors: React.FC = () => <AdminAuthorsTab />;


